import { useTranslation } from "react-i18next"

import { ApiQueryName } from "@/models/api"
import { useRenameQueryResult } from "@/services/utils"
import type { HttpError } from "@/utils/api"
import { showErrorNotification } from "@/utils/notification-bridge"
import { useDownloadFile } from "@/utils/use-download-file"
import { useMutation } from "@tanstack/react-query"
import type { UseMutationOptions } from "@tanstack/react-query"

import type { BillsExportFile } from "./get-bills-export"
import { getBillsExport } from "./get-bills-export"

const GENERIC_EXPORT_ERROR_MESSAGES = new Set([
  "HTTP 422",
  "Request failed with status code 422",
  "Unprocessable Entity",
  "Unprocessable content",
])

const isXlsxExportHref = (href: string): boolean => href.includes("format=xlsx")

const exportErrorNotificationMessage = (
  error: HttpError,
  fallback: string
): string => {
  if (error.status !== 422) {
    return fallback
  }
  if (GENERIC_EXPORT_ERROR_MESSAGES.has(error.message)) {
    return fallback
  }

  return error.message
}

export const useDownloadBillsExport = (
  options?: UseMutationOptions<BillsExportFile, HttpError, string>
) => {
  const downloadFile = useDownloadFile()
  const { t } = useTranslation("sites")

  const mutation = useMutation<BillsExportFile, HttpError, string>({
    ...options,
    meta: { ...options?.meta, handlesOwnErrors: true },
    mutationFn: async (href: string) => getBillsExport(href),
    onError: (error, href, ...rest) => {
      showErrorNotification(
        error,
        exportErrorNotificationMessage(
          error,
          t(
            isXlsxExportHref(href)
              ? "bills.grid.exportExcelError"
              : "bills.grid.exportCsvError"
          )
        )
      )
      options?.onError?.(error, href, ...rest)
    },
    onSuccess: (file, ...rest) => {
      downloadFile(file.blob, file.fileName)
      options?.onSuccess?.(file, ...rest)
    },
    retry: false,
  })

  return useRenameQueryResult(mutation, ApiQueryName.BillsExport)
}
